import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Info, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../../api';

const INITIAL_FORM = {
  nom: '', prenom: '', cin: '', date_naissance: '', emailLocal: '', email: '', login: '', mote_de_passe: '',
  telephone: '', sexe: '', adresse: '', role_id: '', module_id: '', date_d_embauche: '', rib: '', type_contrat: '',
  organisation_id: '', photo: '', domaineSelected: '',
};

const OPTIONS = {
  sex: [
    { value: 'Homme', label: 'Homme' },
    { value: 'Femme', label: 'Femme' }
  ],
  contrat: [
    { value: 'CDI', label: 'CDI' },
    { value: 'CDD', label: 'CDD' },
    { value: 'STAGE', label: 'Stage' }
  ]
};

const VALIDATORS = {
  nom: v => v?.length >= 2 ? null : 'Minimum 2 caractères',
  prenom: v => v?.length >= 2 ? null : 'Minimum 2 caractères',
  cin: v => /^[A-Za-z]{2}\d{4,6}$/.test(v) ? null : 'Format : 2 lettres + 4-6 chiffres',
  date_naissance: v => {
    if (!v) return 'Date requise';
    const birth = new Date(v);
    const today = new Date();
    if (birth > today) return 'Date invalide';
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age >= 18 ? null : 'Âge minimum 18 ans';
  },
  emailLocal: v => v ? null : 'Partie locale requise',
  login: v => v?.length >= 3 ? null : 'Minimum 3 caractères',
  mote_de_passe: v => v?.length >= 6 ? null : 'Minimum 6 caractères',
  telephone: v => /^(06|07|05|\+212)[0-9]{8}$/.test(v) ? null : 'Numéro invalide',
  sexe: v => v ? null : 'Champ obligatoire',
  adresse: v => v?.length >= 5 ? null : 'Adresse trop courte',
  role_id: v => v ? null : 'Champ obligatoire',
  organisation_id: v => v ? null : 'Champ obligatoire',
  date_d_embauche: (v, isClient) => {
    if (isClient) return null;
    if (!v) return 'Date requise';
    const hire = new Date(v);
    const today = new Date();
    if (hire > today) return 'Date dans le futur';
    return null;
  },
  rib: (v, isClient) => (!isClient ? (v ? null : 'Champ obligatoire') : null),
  type_contrat: (v, isClient) => (!isClient ? (v ? null : 'Champ obligatoire') : null),
};

export default function AddEmployee() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ organisations: [], roles: [], modules: [] });
  const [domaines, setDomaines] = useState([]);
  const [organisationData, setOrganisationData] = useState(null);
  const [organisationId, setOrganisationId] = useState(null);
  
    useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setOrganisationId(parseInt(user.organisation_id, 10));
        console.log('organisation ID:', organisationId);
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);

  const filteredRoles = useMemo(() => {
    return data.roles.filter(role => role.id === 1 || role.id === 6);
  }, [data.roles]);

  // Chargement initial des données
  useEffect(() => {
    if (!organisationId) {
      toast.error("Organisation ID non trouvé ");
      return;
    }
    
    // Mise à jour du form avec l'organisation_id
    setForm(prev => ({
      ...prev,
      organisation_id: organisationId
    }));
    
    const endpoints = {
      roles: '/parametrages/roles',
      modules: '/modules'
    };
    
    Promise.all(
      Object.entries(endpoints).map(([key, url]) =>
        api.get(url)
          .then(r => ({ [key]: r.data[key] || r.data }))
          .catch(() => {
            toast.error(`Impossible de charger ${key}`);
            return { [key]: [] };
          })
      )
    ).then(results => setData(Object.assign({}, ...results)));
  }, [organisationId]);

  // Chargement des domaines de l'organisation
  useEffect(() => {
    if (!organisationId) {
      setDomaines([]);
      setOrganisationData(null);
      return;
    }

    api.get(`/organisations/${organisationId}`)
      .then(res => {
        const orgData = res.data.organisation || res.data;
        setOrganisationData(orgData);
        
        const domainesData = orgData.domaines || [];
        setDomaines(domainesData);
        
        // Sélection automatique du premier domaine
        if (domainesData.length > 0) {
          setForm(prev => ({ 
            ...prev, 
            domaineSelected: domainesData[0].nomDomaine 
          }));
        }
      })
      .catch(err => {
        console.error('Erreur lors du chargement de l\'organisation:', err);
        setDomaines([]);
        setOrganisationData(null);
        toast.error('Impossible de charger les données de l\'organisation');
      });
  }, [organisationId]);

  const isClient = useMemo(() => {
    const role = data.roles.find(r => r.id.toString() === form.role_id);
    return role ? /client/i.test(role.nom) : false;
  }, [data.roles, form.role_id]);

  const validate = useCallback(() => {
    const errs = {};
    Object.entries(VALIDATORS).forEach(([k, fn]) => {
      const error = fn(form[k], isClient);
      if (error) errs[k] = error;
    });
    return errs;
  }, [form, isClient]);

  const handleChange = ({ target: { name, value, files } }) => {
    setForm(prev => {
      const nv = name === 'photo' ? files[0] : value;
      const updated = { ...prev, [name]: nv };
      
      // Reset des champs dépendants
      if (name === 'role_id') {
        updated.module_id = '';
      }
      
      return updated;
    });
    setErrors(prev => ({ ...prev, [name]: null }));
  };


  const uploadToLaravel = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return res.data.url; // URL publique renvoyée par Laravel
};

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    // Construction de l'email complet
    const fullForm = {
      ...form,
      email: form.emailLocal && form.domaineSelected
        ? `${form.emailLocal}@${form.domaineSelected}`
        : form.email,
      organisation_id: organisationId 
    };

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error('Veuillez corriger les erreurs');
      setLoading(false);
      return;
    }

    try {
      let photoUrl = '';
      if (fullForm.photo) {
        photoUrl = await uploadToLaravel(fullForm.photo);
      }

      // Construction du payload avec l'organisation_id
      const payload = {
        ...fullForm,
        photo: photoUrl || '',
        organisation_id: organisationId // S'assurer que l'organisation_id est présent
      };

      console.log('Payload envoyé:', payload); // Pour déboggage

      await api.post('/utilisateurs', payload);
      toast.success('Utilisateur ajouté avec succès', { autoClose: 500 });
      navigate('/admin-client/employees');
    } catch (err) {
      console.error('Erreur lors de l\'ajout:', err);
      const res = err.response?.data;
      if (res?.errors) {
        setErrors(res.errors);
      } else {
        toast.error(res?.message || 'Erreur serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const inputClass = field =>
    `w-full px-4 py-2 rounded-lg border focus:ring-2 ${errors[field]
      ? 'border-red-500 focus:ring-red-200'
      : 'border-gray-300 focus:border-terrecuite focus:ring-terrecuite/20'
    }`;

  const render = (name, label, type = 'text', opts = {}) => {
    const { select, options = [], ...rest } = opts;
    const extra = type === 'date' ? { max: todayStr } : {};
    
    if (type === 'textarea') {
      return (
        <div key={name}>
          <label className="block text-sm font-medium mb-2 text-gray-700">
            {label}{opts.required !== false && <span className="text-red-500">*</span>}
          </label>
          <textarea
            name={name}
            value={form[name] || ''}
            onChange={handleChange}
            className={inputClass(name)}
            {...rest}
          />
          {errors[name] && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />{errors[name]}
            </p>
          )}
        </div>
      );
    }
    
    if (type === 'file') {
      return (
        <div key={name}>
          <label className="block text-sm font-medium mb-2 text-gray-700">
            {label}{opts.required !== false && <span className="text-red-500">*</span>}
          </label>
          <input
            type="file"
            name={name}
            onChange={handleChange}
            className={inputClass(name)}
            {...extra}
            {...rest}
          />
          {errors[name] && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />{errors[name]}
            </p>
          )}
        </div>
      );
    }
    
    return (
      <div key={name}>
        <label className="block text-sm font-medium mb-2 text-gray-700">
          {label}{opts.required !== false && <span className="text-red-500">*</span>}
        </label>
        {select ? (
          <select name={name} value={form[name]} onChange={handleChange} className={inputClass(name)}>
            <option value="">-- Sélectionner --</option>
            {options.map(o => (
              <option key={o.value || o.id} value={o.value || o.id}>
                {o.label || o.nom}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            name={name}
            value={form[name] || ''}
            onChange={handleChange}
            className={inputClass(name)}
            {...extra}
            {...rest}
          />
        )}
        {errors[name] && (
          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />{errors[name]}
          </p>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 w-full max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
        <CheckCircle className="inline-block w-8 h-8 text-terrecuite mr-2" />
        Ajouter un Utilisateur
        {organisationData && (
          <span className="block text-sm text-gray-600 mt-2">
            Organisation: {organisationData.nom}
          </span>
        )}
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {render('nom', 'Nom')}
        {render('prenom', 'Prénom')}
        {render('cin', 'CIN', 'text', { placeholder: 'AB12345' })}
        {render('date_naissance', 'Date de naissance', 'date')}
        
        <div key="emailLocal">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Email<span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              name="emailLocal"
              type="text"
              value={form.emailLocal}
              onChange={handleChange}
              className={`${inputClass('emailLocal')} flex-grow`}
              placeholder="nom"
            />
            <select
              name="domaineSelected"
              value={form.domaineSelected}
              onChange={handleChange}
              className={`${inputClass('domaineSelected')} w-48`}
              disabled={domaines.length === 0}
            >
              {domaines.length === 0 ? (
                <option value="">Aucun domaine</option>
              ) : (
                domaines.map(d => (
                  <option key={d.id} value={d.nomDomaine}>
                    @{d.nomDomaine}
                  </option>
                ))
              )}
            </select>
          </div>
          {(errors.emailLocal || errors.domaineSelected) && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.emailLocal || errors.domaineSelected}
            </p>
          )}
          {form.emailLocal && form.domaineSelected && (
            <p className="text-sm text-gray-600 mt-1">
              Email complet: {form.emailLocal}@{form.domaineSelected}
            </p>
          )}
        </div>
        
        {render('login', 'Login')}
        {render('mote_de_passe', 'Mot de passe', 'password', { placeholder: '••••••••' })}
        {render('telephone', 'Téléphone', 'tel')}
        {render('sexe', 'Sexe', 'select', { select: true, options: OPTIONS.sex })}
        {render('adresse', 'Adresse', 'textarea', { placeholder: 'Adresse' })}
        {render('role_id', 'Rôle', 'select', { 
          select: true, 
          options: filteredRoles.map(r => ({ value: r.id, label: r.nom }))
        })}
        {render('photo', 'Photo', 'file', { accept: 'image/*', required: false })}
        
      </div>
      
      <div className="mt-8 flex justify-end gap-4">
        <button 
          type="button" 
          onClick={() => navigate(-1)} 
          className="px-6 py-2 border rounded-lg hover:bg-gray-50"
        >
          Annuler
        </button>
        <button 
          type="submit" 
          disabled={loading} 
          className="px-6 py-2 bg-terrecuite text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
        >
          {loading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          <span>{loading ? 'Envoi...' : 'Ajouter'}</span>
        </button>
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-terrecuite flex-shrink-0" />
        <div>
          <p className="text-sm text-terrecuite">
            Tous les champs marqués* sont obligatoires.
          </p>
          {organisationId && (
            <p className="text-xs text-gray-600 mt-1">
              Organisation ID: {organisationId}
            </p>
          )}
        </div>
      </div>
    </form>
  );
}